/**
 * System detail drilldown for one EHR connection: a header card with status,
 * stat tiles (Overview only) and a tab bar, then the active tab's panel.
 * The Review queue is fetched only while its tab is open.
 */
import {
  Box,
  Button,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';

import * as ROUTES from '@constants/routes';

import {
  isServiceUnreachable,
  useEhrSyncConnection,
} from '@hooks/api/ehr-sync';
import {
  useEhrReviewQueue,
  useEhrSyncPatientLinks,
} from '@hooks/api/ehr-sync-patients';
import { useNavigate } from '@hooks/useNavigate';

import { formatDate } from '@utils/format';

import EhrSyncStatusChip from '../../components/EhrSyncStatusChip';
import RegistryStateChip from '../../components/RegistryStateChip';
import { commonMessages, ehrEnvironmentMessages } from '../../messages';
import RelativeTime from '../shared/RelativeTime';
import StatePanel from '../shared/StatePanel';
import { organizationSubline } from '../shared/monitoring.util';
import PatientsTab from './PatientsTab';
import ResourceConfigTable from './ResourceConfigTable';
import ResourcePolicyTab from './ResourcePolicyTab';
import ReviewQueueTab from './ReviewQueueTab';
import SyncHistoryTable from './SyncHistoryTable';

const messages = defineMessages({
  back: {
    id: 'ehrSync.detail.back',
    defaultMessage: 'All integrations',
  },
  unreachableBody: {
    id: 'ehrSync.detail.unreachableBody',
    defaultMessage:
      "This connection's status couldn't be loaded because the integration service didn't respond. Try again in a moment.",
  },
  notFound: {
    id: 'ehrSync.detail.notFound',
    defaultMessage: 'EHR connection not found.',
  },
  patients: {
    id: 'ehrSync.detail.stat.patients',
    defaultMessage: 'Patients',
  },
  patientsNote: {
    id: 'ehrSync.detail.stat.patientsNote',
    defaultMessage: 'active in this organization',
  },
  messagesReceived: {
    id: 'ehrSync.detail.stat.messagesInRetention',
    defaultMessage: 'Messages in retention',
  },
  messagesReceivedNote: {
    id: 'ehrSync.detail.stat.messagesReceivedNote',
    defaultMessage: 'across all channels',
  },
  lastMessage: {
    id: 'ehrSync.detail.stat.lastMessage',
    defaultMessage: 'Last message',
  },
  channelsRunning: {
    id: 'ehrSync.detail.stat.channelsRunning',
    defaultMessage: 'Channels running',
  },
  channelsRunningValue: {
    id: 'ehrSync.detail.stat.channelsRunningValue',
    defaultMessage: '{running} of {total}',
  },
  channelsRunningNote: {
    id: 'ehrSync.detail.stat.channelsRunningNote',
    defaultMessage: 'one channel per resource',
  },
  queuedErrors: {
    id: 'ehrSync.detail.stat.queuedErrors',
    defaultMessage: 'Queued errors',
  },
  queuedErrorsNote: {
    id: 'ehrSync.detail.stat.queuedErrorsNote',
    defaultMessage: 'messages in error queues',
  },
  queuedErrorsClear: {
    id: 'ehrSync.detail.stat.queuedErrorsClear',
    defaultMessage: 'queues clear',
  },
  tabsLabel: {
    id: 'ehrSync.detail.tabs.label',
    defaultMessage: 'Connection sections',
  },
  tabOverview: {
    id: 'ehrSync.detail.tabs.overview',
    defaultMessage: 'Overview',
  },
  tabResourcePolicy: {
    id: 'ehrSync.detail.tabs.resourcePolicy',
    defaultMessage: 'Resource policy',
  },
  tabPatients: {
    id: 'ehrSync.detail.tabs.patients',
    defaultMessage: 'Patients',
  },
  tabReviewQueue: {
    id: 'ehrSync.detail.tabs.reviewQueue',
    defaultMessage: 'Review queue',
  },
});

/** Sections of the connection detail view, one per tab. */
type DetailTab = 'overview' | 'resourcePolicy' | 'patients' | 'reviewQueue';

/**
 * Tab bar order. `disabled` tabs render but MUST NOT be selectable. Their
 * panels are not built yet.
 */
const DETAIL_TABS: {
  value: DetailTab;
  label: (typeof messages)[keyof typeof messages];
  disabled: boolean;
}[] = [
  { value: 'overview', label: messages.tabOverview, disabled: false },
  {
    value: 'resourcePolicy',
    label: messages.tabResourcePolicy,
    disabled: false,
  },
  { value: 'patients', label: messages.tabPatients, disabled: false },
  { value: 'reviewQueue', label: messages.tabReviewQueue, disabled: false },
];

/** DOM id of a tab button. */
const tabId = (value: DetailTab) => `ehr-sync-detail-tab-${value}`;
/** DOM id of a tab's panel. */
const tabPanelId = (value: DetailTab) => `ehr-sync-detail-tabpanel-${value}`;

/** Props for {@link MonitoringDetail}. */
interface MonitoringDetailProps {
  /** QIE zone id of the connection, from the route. */
  connectionId?: string;
}

/**
 * Detail view for one EHR connection, with loading, error and not-found states.
 *
 * @param props - see {@link MonitoringDetailProps}
 */
export default function MonitoringDetail({
  connectionId,
}: MonitoringDetailProps) {
  const { formatMessage, formatNumber } = useIntl();
  const [tab, setTab] = useState<DetailTab>('overview');
  // The links carry patient demographics, so they are fetched only once the
  // Patients tab has been opened, then kept so its count stays on the tab.
  const [patientsOpened, setPatientsOpened] = useState(false);
  const { data, loading, error, refetch } = useEhrSyncConnection(connectionId);
  const patientLinks = useEhrSyncPatientLinks(
    patientsOpened ? data?.ehrConnectionId : null
  );
  const linksReady =
    patientsOpened && !patientLinks.loading && !patientLinks.error;
  // Same lazy rule as the links: the queue is fetched once its tab is opened.
  const [reviewOpened, setReviewOpened] = useState(false);
  const [reviewLinkId, setReviewLinkId] = useState<string | null>(null);
  const reviewQueue = useEhrReviewQueue(
    reviewOpened ? data?.ehrConnectionId : null
  );
  const { navigateRoute } = useNavigate();

  const backLink = (
    <Button
      variant="text"
      size="small"
      startIcon={<Icon name="chevron-left" color="primary.main" type="fas" />}
      onClick={() => navigateRoute(ROUTES.INTEGRATIONS)}
      sx={{ mb: 1.5 }}
    >
      {formatMessage(messages.back)}
    </Button>
  );

  if (!loading && error) {
    // Distinct from not-found: the connection may exist, the service just did
    // not answer. Keep the way back visible.
    const unreachable = isServiceUnreachable(error);
    return (
      <Box>
        {backLink}
        <StatePanel
          variant="error"
          title={formatMessage(
            unreachable
              ? commonMessages.unreachableTitle
              : commonMessages.loadFailedTitle
          )}
          body={
            unreachable
              ? formatMessage(messages.unreachableBody)
              : error.message
          }
          onRetry={refetch}
        />
      </Box>
    );
  }

  if (!loading && !data) {
    return (
      <Box>
        {backLink}
        <StatePanel variant="empty" title={formatMessage(messages.notFound)} />
      </Box>
    );
  }

  // Tab counts wait for their data, so a load error never reads as zero.
  const tabLabel = (value: DetailTab, label: string) => {
    if (value === 'patients' && linksReady) {
      return `${label} ${formatNumber(patientLinks.data.length)}`;
    }
    // The connection's count, even once the queue is loaded, so the tab never
    // flips between two sources. It MAY exceed the queue by unreadable links.
    if (
      value === 'reviewQueue' &&
      data?.reviewCount !== null &&
      data?.reviewCount !== undefined
    ) {
      return `${label} ${formatNumber(data.reviewCount)}`;
    }
    return label;
  };

  const openTab = (value: DetailTab) => {
    setTab(value);
    // A case opened from a Patients row is a one-off request.
    if (value !== 'reviewQueue') {
      setReviewLinkId(null);
    }
    if (value === 'patients') {
      setPatientsOpened(true);
    }
    if (value === 'reviewQueue') {
      // The query stays mounted once opened, so pick up cases QIE has added
      // since.
      if (reviewOpened) {
        reviewQueue.refetch();
      }
      setReviewOpened(true);
    }
  };

  const count = (value?: number | null) =>
    value === null || value === undefined ? '-' : formatNumber(value);

  const runningCount =
    data?.resources.filter((resource) => resource.state === 'running').length ??
    0;

  const subline = data
    ? organizationSubline(data.organizationName, [
        data.network,
        data.ehrVendor,
        data.ehrEnvironment
          ? formatMessage(ehrEnvironmentMessages[data.ehrEnvironment])
          : null,
        data.description,
      ])
    : '';
  // lastSyncAt is the last successful run; the tile is about traffic, so it
  // prefers the last message received.
  const lastMessageAt = data?.lastReceivedAt ?? data?.lastSyncAt;

  const stats: { label: string; value: React.ReactNode; note: string }[] = data
    ? [
        {
          label: formatMessage(messages.patients),
          value: count(data.patientCount),
          note: formatMessage(messages.patientsNote),
        },
        {
          label: formatMessage(messages.messagesReceived),
          value: count(data.recordCount),
          note: formatMessage(messages.messagesReceivedNote),
        },
        {
          label: formatMessage(messages.lastMessage),
          value: <RelativeTime iso={lastMessageAt} />,
          note: lastMessageAt
            ? formatDate({ date: lastMessageAt, formatter: 'datetime' })
            : '-',
        },
        {
          label: formatMessage(messages.channelsRunning),
          value: formatMessage(messages.channelsRunningValue, {
            running: runningCount,
            total: data.resources.length,
          }),
          note: formatMessage(messages.channelsRunningNote),
        },
        {
          label: formatMessage(messages.queuedErrors),
          value: count(data.errorCount),
          note: formatMessage(
            data.errorCount
              ? messages.queuedErrorsNote
              : messages.queuedErrorsClear
          ),
        },
      ]
    : [];

  return (
    // Only the first load shows the loader. A refetch after a policy toggle
    // keeps the page mounted so focus and the tab stay where they were.
    <ContentLoader loading={loading && !data}>
      {data ? (
        <Box>
          {backLink}

          <Paper variant="outlined" sx={{ p: 3, mb: 2.25 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'flex-start' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  {data.organizationName}
                </Typography>
                {subline ? (
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {subline}
                  </Typography>
                ) : null}
              </Box>
              <Stack alignItems={{ sm: 'flex-end' }} spacing={1}>
                <EhrSyncStatusChip status={data.status} />
                {data.registryState && data.registryState !== 'active' ? (
                  <RegistryStateChip state={data.registryState} />
                ) : null}
              </Stack>
            </Stack>

            {tab === 'overview' ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    md: 'repeat(5, 1fr)',
                  },
                  gap: '1px',
                  bgcolor: 'divider',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mt: 2.5,
                  overflow: 'hidden',
                }}
              >
                {stats.map((stat) => (
                  <Box
                    key={stat.label}
                    sx={{ bgcolor: 'background.paper', p: 2 }}
                  >
                    <Typography
                      variant="overline"
                      fontWeight="bold"
                      color="text.secondary"
                      lineHeight={1.5}
                    >
                      {stat.label}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" mt={0.75}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.note}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}

            <Tabs
              value={tab}
              onChange={(_event, value: DetailTab) => openTab(value)}
              aria-label={formatMessage(messages.tabsLabel)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                mt: 2.5,
                mx: -3,
                mb: -3,
                px: 1.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              {DETAIL_TABS.map((item) => (
                <Tab
                  key={item.value}
                  value={item.value}
                  label={tabLabel(item.value, formatMessage(item.label))}
                  disabled={item.disabled}
                  id={tabId(item.value)}
                  // Disabled tabs have no panel yet, so nothing to control.
                  aria-controls={
                    item.disabled ? undefined : tabPanelId(item.value)
                  }
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                />
              ))}
            </Tabs>
          </Paper>

          <Box
            role="tabpanel"
            id={tabPanelId('overview')}
            aria-labelledby={tabId('overview')}
            hidden={tab !== 'overview'}
          >
            {data.errorSummary ? (
              <Box
                role="alert"
                sx={(theme) => ({
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  border: 1,
                  borderColor: alpha(theme.palette.error.main, 0.35),
                  borderLeft: `4px solid ${theme.palette.error.main}`,
                  borderRadius: 1,
                  p: 2,
                  mb: 2.25,
                })}
              >
                <Icon
                  name="triangle-exclamation"
                  color="error.main"
                  type="fas"
                />
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="error.main"
                  >
                    {data.errorSummary.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.375}>
                    {data.errorSummary.body}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            <ResourceConfigTable resources={data.resources} />
            <Box sx={{ mt: 2.25 }}>
              <SyncHistoryTable history={data.history ?? []} />
            </Box>
          </Box>

          <Box
            role="tabpanel"
            id={tabPanelId('resourcePolicy')}
            aria-labelledby={tabId('resourcePolicy')}
            hidden={tab !== 'resourcePolicy'}
          >
            {tab === 'resourcePolicy' ? (
              <ResourcePolicyTab
                ehrConnectionId={data.ehrConnectionId}
                resources={data.resources}
              />
            ) : null}
          </Box>

          <Box
            role="tabpanel"
            id={tabPanelId('patients')}
            aria-labelledby={tabId('patients')}
            hidden={tab !== 'patients'}
          >
            {tab === 'patients' ? (
              <PatientsTab
                ehrConnectionId={data.ehrConnectionId}
                organizationName={data.organizationName}
                connectionLabel={data.ehrVendor}
                vendor={data.ehrVendor}
                links={patientLinks.data}
                loading={patientLinks.loading}
                error={patientLinks.error}
                onRetry={patientLinks.refetch}
                onOpenReviewQueue={(linkId) => {
                  setReviewLinkId(linkId);
                  openTab('reviewQueue');
                }}
              />
            ) : null}
          </Box>

          <Box
            role="tabpanel"
            id={tabPanelId('reviewQueue')}
            aria-labelledby={tabId('reviewQueue')}
            hidden={tab !== 'reviewQueue'}
          >
            {tab === 'reviewQueue' ? (
              <ReviewQueueTab
                cases={reviewQueue.data}
                loading={reviewQueue.loading}
                error={reviewQueue.error}
                onRetry={reviewQueue.refetch}
                vendor={data.ehrVendor}
                initialLinkId={reviewLinkId}
              />
            ) : null}
          </Box>
        </Box>
      ) : null}
    </ContentLoader>
  );
}
